/**
 * The L0 strip of the Application-experience board: one tile per app the
 * browser extension reports on, each a door into the panels below.
 *
 * Every tile is built by `experienceTile()` so the honesty decisions live in a
 * tested pure module rather than in this JSX (see `experience-format.ts`):
 *
 * - An app nobody posted for is **silent**, and its tile says so instead of
 *   showing four em dashes that read as "measured, and empty".
 * - A `null` median is an em dash on a neutral tone. It never becomes `0 ms` and
 *   it never goes green.
 * - `error_rate: null` is unknown; only a measured `0` may go green.
 * - Coverage exists for F&O alone, because nothing in this workspace counts the
 *   people who used Power BI, SharePoint, Teams or CE *without* the extension.
 *   The other four tiles say the ratio has no denominator rather than drawing an
 *   empty meter, and their user counts are labelled as lower bounds.
 */

import { Card, Meter, Statistic, Typography } from "@norican/react";

// Durations wear the console's grammar (`420 ms`, `6.2 s`), so the two boards
// never print the same number two ways.
import { formatDurationMs } from "@/features/dynamics/console-format";
import type { FoConsoleExperienceApp } from "@/lib/api";
import { rumAppLabel } from "@/lib/rum-apps";

import { EM_DASH, type ExperienceTile, experienceTile, formatCount } from "./experience-format";

interface AppTilesProps {
  apps: readonly FoConsoleExperienceApp[];
  /** Whether this app has a coverage denominator (F&O today). */
  hasDenominator: (app: string) => boolean;
  /** The app the board is narrowed to, or `null` for all. */
  selected: string | null;
  /** Narrow the board to one app, or clear the filter by picking it again. */
  onSelect: (app: string | null) => void;
}

export function AppTiles({ apps, hasDenominator, selected, onSelect }: AppTilesProps) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {apps.map((row) => (
        <AppTile
          key={row.app}
          tile={experienceTile(row, hasDenominator(row.app))}
          selected={selected === row.app}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

interface AppTileProps {
  tile: ExperienceTile;
  selected: boolean;
  onSelect: (app: string | null) => void;
}

function AppTile({ tile, selected, onSelect }: AppTileProps) {
  const label = rumAppLabel(tile.app);
  return (
    <button
      type="button"
      className="block h-full rounded text-left focus-visible:outline-2"
      aria-pressed={selected}
      onClick={() => onSelect(selected ? null : tile.app)}
      aria-label={
        selected
          ? `Clear the ${label} filter`
          : `Narrow the trend, page keys and probes to ${label}`
      }
    >
      <Card
        variant={selected ? "elevated" : "outline"}
        accent="left"
        accentTone={tile.state === "silent" ? "neutral" : tile.ttfbTone}
        interactive
        className="h-full"
      >
        <Card.Body>
          <div className="flex flex-col gap-3">
            <Statistic
              label={label}
              value={formatDurationMs(tile.ttfb_p50_ms)}
              tone={tile.state === "silent" ? "neutral" : tile.ttfbTone}
              size="lg"
              caption={
                tile.state === "silent"
                  ? tile.caption
                  : `TTFB p50 · p95 ${formatDurationMs(tile.ttfb_p95_ms)} · ${tile.caption}`
              }
            />

            {tile.state === "reporting" && (
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <Figure
                  label="Interaction p50"
                  value={formatDurationMs(tile.interaction_p50_ms)}
                  note={
                    tile.interaction_p50_ms == null
                      ? "not measured"
                      : `p75 ${formatDurationMs(tile.interaction_p75_ms)}`
                  }
                />
                <Figure
                  label="Error rate"
                  value={tile.errorRateText}
                  note={
                    tile.error_rate == null
                      ? "requests not counted — unknown, not clean"
                      : "of same-host requests"
                  }
                />
                <Figure
                  label="Browsers reporting"
                  value={formatCount(tile.users)}
                  note={
                    tile.coverage.applicable
                      ? `${formatCount(tile.minutes)} user-minutes`
                      : "a lower bound on the fleet"
                  }
                />
                <Figure
                  label="Over HTTP/3"
                  value={tile.h3ShareText}
                  note={tile.h3_share == null ? "protocol not reported" : "request-weighted"}
                />
              </dl>
            )}

            {tile.coverage.applicable ? (
              <div className="flex flex-col gap-1">
                <Meter.Root
                  value={tile.coverage.pct ?? 0}
                  min={0}
                  max={100}
                  size="sm"
                  tone={
                    tile.coverage.pct == null
                      ? "accent"
                      : tile.coverage.pct >= 75
                        ? "success"
                        : tile.coverage.pct >= 40
                          ? "warning"
                          : "danger"
                  }
                  aria-valuetext={`Browsers reporting for ${label}: ${tile.coverage.text}`}
                >
                  <div className="mb-1 flex items-baseline justify-between gap-3">
                    <Meter.Label>
                      <Typography variant="caption" tone="muted">
                        Browsers reporting
                      </Typography>
                    </Meter.Label>
                    <Meter.Value>
                      {() => (
                        <Typography variant="body-sm" as="span" tabular className="font-semibold">
                          {tile.coverage.text}
                        </Typography>
                      )}
                    </Meter.Value>
                  </div>
                  <Meter.Track>
                    <Meter.Indicator />
                  </Meter.Track>
                </Meter.Root>
                <Typography variant="caption" tone="muted">
                  {tile.coverage.caption}
                </Typography>
              </div>
            ) : (
              <Typography variant="caption" tone="muted">
                Coverage: {tile.coverage.text}. {tile.coverage.caption}
              </Typography>
            )}
          </div>
        </Card.Body>
      </Card>
    </button>
  );
}

function Figure({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-fg-muted text-xs">{label}</dt>
      <dd className="text-fg font-semibold tabular-nums">{value === "" ? EM_DASH : value}</dd>
      <dd className="text-fg-subtle text-xs">{note}</dd>
    </div>
  );
}
